import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { GetPaidOnline } from './GetPaidOnline';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { LINK_CLASS } from '@league/ui';

import { Button, ErrorNote } from '../../components/ui';
import { ApiError, apiFetch, apiPatch } from '../../lib/api';
import { Membership } from './Membership';

/**
 * A STAFF ACCOUNT — hers, and short.
 *
 * The shortness is the design rather than an unfinished screen, and every absence on it is
 * explained in a sentence rather than left as a gap somebody has to wonder about.
 *
 * HER NAME IS HERS. It is on the roster, in the trail, at the foot of every email she copies.
 *
 * HER EMAIL IS NOT, and the page says why. It is her sign-in address AND where the six-digit code
 * arrives, so an unverified change to it is a complete account takeover — type a new address,
 * receive the next code there, and the account is gone. Doing it properly means confirming at the
 * new address first, which is real work that cannot finish before the domain exists.
 *
 * THE SECOND FACTOR IS STATED, NOT OFFERED. A switch that refuses to move is worse than a sentence
 * saying it is required and why.
 *
 * Essential account and billing messages are explained without offering unrelated preference switches.
 */

interface Account {
  id: string;
  name: string;
  email: string;
  role: string;
  mfaEnrolled: boolean;
  mfaRequired: boolean;
}

/**
 * `nightsOnly`: an instructor's account (2026-10-16). The same screen without what is not hers — no
 * membership, no getting paid online, no billing email — because she pays nothing and is paid by her
 * director, not through the platform.
 */
export function AccountSettings({ onSignOut, nightsOnly = false }: { onSignOut: () => void; nightsOnly?: boolean }) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);
  const meta = useQuery<{ brand: string; siteOrigin?: string }>({
    queryKey: ['meta'],
    queryFn: () => apiFetch('/api/meta'),
    staleTime: Infinity,
    enabled: false,
  });
  const contactUrl = meta.data?.siteOrigin
    ? `${meta.data.siteOrigin.replace(/\/$/, '')}/contact`
    : undefined;

  const account = useQuery({
    queryKey: ['account'],
    queryFn: () => apiFetch<{ account: Account }>('/api/me/account'),
  });

  useEffect(() => {
    if (account.data?.account) setName(account.data.account.name);
  }, [account.data]);

  /* From "Start your membership" (2026-11-01): the router follows the address, not the fragment. */
  const { hash } = useLocation();
  const loaded = Boolean(account.data);
  useEffect(() => {
    if (loaded && hash === '#membership') document.getElementById('membership')?.scrollIntoView();
  }, [loaded, hash]);

  const save = useMutation({
    mutationFn: (input: { name: string }) => apiPatch<{ account: Account }>('/api/me/account', input),
    onSuccess: async () => {
      setSaved(true);
      /* Her name is in the rail, in the audit trail and on every note she has written. */
      await queryClient.invalidateQueries();
    },
  });

  if (account.isPending) return <p className="px-6 py-10 text-sm text-ink-3">Loading…</p>;

  const me = account.data?.account;
  if (!me) return null;

  const error = save.error instanceof ApiError ? save.error.message : null;
  const unchanged = name.trim() === me.name;

  return (
    <div className="flex max-w-[44rem] flex-col gap-8 px-6 py-8">
      <header>
        <h1 className="font-display text-3xl leading-tight">{nightsOnly ? 'Your account' : 'Settings'}</h1>
        {nightsOnly ? null : <p className="mt-1 text-[14px] text-ink-2">Your profile, membership, and sign-in details.</p>}
      </header>

      {error ? <ErrorNote>{error}</ErrorNote> : null}

      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          Your profile
        </h2>

        <div className="mt-3 flex flex-col divide-y divide-light rounded-md border border-light bg-paper">
          {/*
            Stacked deliberately rather than half-wrapped. The explanations here are a sentence
            each — that is the whole point of the screen — and a label-beside-control row cannot
            hold one at this width without collapsing at some arbitrary breakpoint and looking
            like it broke.
          */}
          <label className="flex flex-col gap-2.5 px-5 py-4">
            <span>
              <span className="block text-[14px] font-medium">Your name</span>
              <span className="block max-w-[32rem] text-[13px] leading-relaxed text-ink-2">
                The name players see on messages you send through {meta.data?.brand ?? 'League'}.
              </span>
            </span>
            <input
              value={name}
              onChange={(event) => {
                setSaved(false);
                setName(event.target.value);
              }}
              aria-label="Your name"
              className="h-11 w-full max-w-64 min-w-0 rounded-md border border-control-border bg-chalk px-3 text-[14px]"
            />
          </label>

          {/*
            Shown, not editable, and the reason is given. A greyed-out field with no explanation
            reads as something broken; a sentence reads as a decision.
          */}
          <div className="flex flex-col gap-2.5 px-5 py-4">
            <span>
              <span className="block text-[14px] font-medium">Your email</span>
              <span className="block max-w-[32rem] text-[13px] leading-relaxed text-ink-2">
                We use this email for sign-in codes and important account updates. Need to change it?{' '}
                {contactUrl ? <a href={contactUrl} className={LINK_CLASS}>Get in touch</a> : 'Get in touch'}{' '}
                and we’ll help.
              </span>
            </span>
            <span className="flex min-h-11 w-full max-w-64 min-w-0 items-center break-all py-2 rounded-md border border-light bg-sand px-3 text-[14px] text-ink-2">
              {me.email}
            </span>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4">
          <Button disabled={save.isPending || unchanged || name.trim() === ''} onClick={() => save.mutate({ name: name.trim() })}>
            {save.isPending ? 'Saving…' : 'Save changes'}
          </Button>
          {saved ? <span className="text-[13px] text-sage">Saved.</span> : null}
        </div>
      </section>

      {nightsOnly ? null : (
        <section id="membership">
          <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
            Your membership
          </h2>
          <Membership />
        </section>
      )}

      {nightsOnly ? null : <GetPaidOnline />}

      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          Signing in
        </h2>

        <div className="mt-3 rounded-md border border-light bg-paper px-5 py-4">
          <p className="text-[14px] font-medium">
            A little extra protection for your account.
          </p>
          <p className="mt-1.5 max-w-[34rem] text-[13px] leading-relaxed text-ink-2">
            After entering your password, you’ll receive a six-digit code by email. This extra
            step helps protect your account and your players’ information.
          </p>
        </div>
      </section>

      {nightsOnly ? null : <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          What we email you
        </h2>

        <div className="mt-3 rounded-md border border-light bg-paper px-5 py-4">
          <p className="max-w-[34rem] text-[13px] leading-relaxed text-ink-2">
            We’ll email you when you need to sign in or manage your account, and when there’s
            something important to know about your membership or payments.
          </p>
        </div>
      </section>}

      <section>
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.14em] text-ink-3">
          Account access
        </h2>
        <div className="mt-3">
          <Button variant="secondary" onClick={onSignOut}>
            Sign out
          </Button>
        </div>
      </section>
    </div>
  );
}
